/**
 * EpicBreakdownSection — View D (RAISE-16867 D5/D6/D9).
 *
 * Primary table: one row per story (ref, repo, sessions, cost, last date).
 * Sortable by cost, ref, or date. Plan-gate notice on 403.
 * Epic rollup (aggregateByEpic) renders only when ≥1 event has a non-null epic.
 */

import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronUp, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  aggregateByStory,
  aggregateByEpic,
  type StoryCostEvent,
  type StoryAggregate,
  type EpicAggregate,
} from "@/lib/cost/storyCostEvents";
import { formatCost } from "@/lib/format";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EpicBreakdownSectionProps {
  events: StoryCostEvent[];
  loading: boolean;
  error: string | null;
  planGated: boolean;
  truncated: boolean;
  total: number;
}

type SortKey = "costUsd" | "ref" | "lastDate";
type SortDir = "asc" | "desc";

// ---------------------------------------------------------------------------
// Sort helper
// ---------------------------------------------------------------------------

function sortStories(
  rows: StoryAggregate[],
  key: SortKey,
  dir: SortDir,
): StoryAggregate[] {
  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (key === "costUsd") {
      cmp = a.costUsd - b.costUsd;
    } else if (key === "ref") {
      cmp = a.ref.localeCompare(b.ref);
    } else {
      cmp = (a.lastDate ?? "").localeCompare(b.lastDate ?? "");
    }
    return dir === "asc" ? cmp : -cmp;
  });
}

// ---------------------------------------------------------------------------
// Column header with sort toggle
// ---------------------------------------------------------------------------

interface SortHeaderProps {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
}

function SortHeader({ label, sortKey, currentKey, currentDir, onSort }: SortHeaderProps) {
  const active = currentKey === sortKey;
  return (
    <TableHead
      className="cursor-pointer select-none"
      onClick={() => onSort(sortKey)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active && (
          currentDir === "asc" ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        )}
      </span>
    </TableHead>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function EpicBreakdownSection({
  events,
  loading,
  error,
  planGated,
  truncated,
  total,
}: EpicBreakdownSectionProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>("costUsd");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  const storyRows = useMemo(() => {
    const aggregated = aggregateByStory(events);
    return sortStories(aggregated, sortKey, sortDir);
  }, [events, sortKey, sortDir]);

  // Epic rollup — only shown when ≥1 event carries a non-null epic (D6)
  const epicRows = useMemo((): EpicAggregate[] => {
    if (!events.some((e) => e.epic !== null)) return [];
    return aggregateByEpic(events).sort((a, b) => b.costUsd - a.costUsd);
  }, [events]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          {t("costPage.epicBreakdown.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {planGated ? (
          <p className="text-sm text-muted-foreground">
            {t("costPage.epicBreakdown.planGate")}
          </p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : storyRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("costPage.epicBreakdown.empty")}
          </p>
        ) : (
          <>
            {truncated && (
              <p className="mb-2 text-xs text-muted-foreground">
                {t("costPage.epicBreakdown.capNote", {
                  shown: events.length,
                  total,
                })}
              </p>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader
                    label={t("costPage.epicBreakdown.columnRef")}
                    sortKey="ref"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                  <TableHead>
                    {t("costPage.epicBreakdown.columnRepo")}
                  </TableHead>
                  <TableHead>
                    {t("costPage.epicBreakdown.columnSessions")}
                  </TableHead>
                  <SortHeader
                    label={t("costPage.epicBreakdown.columnCost")}
                    sortKey="costUsd"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={t("costPage.epicBreakdown.columnDate")}
                    sortKey="lastDate"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {storyRows.map((row) => (
                  <TableRow key={row.ref}>
                    <TableCell className="font-mono text-xs">{row.ref}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.repoSlug ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs">{row.sessions}</TableCell>
                    <TableCell className="text-xs font-medium">
                      {formatCost(row.costUsd)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.lastDate ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Epic rollup — visible only when ≥1 event has a non-null epic (D6) */}
            {epicRows.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t("costPage.epicBreakdown.epicRollupTitle")}
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">{t("costPage.epicBreakdown.columnEpic")}</TableHead>
                      <TableHead className="text-xs">{t("costPage.epicBreakdown.columnStories")}</TableHead>
                      <TableHead className="text-xs">{t("costPage.epicBreakdown.columnCost")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {epicRows.map((row) => (
                      <TableRow key={row.epic}>
                        <TableCell className="font-mono text-xs">{row.epic}</TableCell>
                        <TableCell className="text-xs">{row.stories}</TableCell>
                        <TableCell className="text-xs font-medium">{formatCost(row.costUsd)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
